import { useAuth } from '../../auth/AuthProvider.jsx';
import { Permission } from '../../auth/roles.js';
import { TabButton } from '../../components/ui/Tabs.jsx';
import {
  MANUALLY_EXCLUDED_FROM, MANUALLY_EXCLUDED_REASON, MANUALLY_HANDLED_FROM, STATUS_TABS,
} from './constants.js';

// Every selected record must be in an eligible source status before we offer
// "Mark Handled" — the backend silently skips ineligible ids, and a button
// that half-works is worse than one that isn't shown.
function allEligibleForManualHandling(selectedStatuses) {
  if (!selectedStatuses || selectedStatuses.size === 0) return false;
  return [...selectedStatuses].every((s) => MANUALLY_HANDLED_FROM.includes(s));
}

function allEligibleForManualExclusion(selectedStatuses) {
  if (!selectedStatuses || selectedStatuses.size === 0) return false;
  return [...selectedStatuses].every((s) => MANUALLY_EXCLUDED_FROM.includes(s));
}

// Undo Exclusion is offered ONLY when every selected record is a MANUAL
// exclusion. Engine-derived exclusions (site_excluded, non_billable_payroll, …)
// are rebuilt from reference data every night — "undoing" one would bounce
// straight back on the next reprocess while parking a non-billable record on
// Pending in the meantime.
function allManuallyExcluded(selectedExclusionReasons) {
  if (!selectedExclusionReasons || selectedExclusionReasons.size === 0) return false;
  return [...selectedExclusionReasons].every((r) => r === MANUALLY_EXCLUDED_REASON);
}

// Action-bar visibility rules:
//
//   pending  → Approve only. (Records are reviewed, not yet in push queue.)
//   approved → Push to NS only. (Already approved; only thing left is push.)
//   gap      → Mark Excluded only. Gaps still can't be APPROVED — they must be
//              resolved upstream (fix mapping + reprocess) so the record moves
//              into pending; approving a gap record bypasses the safety check.
//              But ops can decide a gap is simply not billable and take it off
//              the list for good (2026-07-28).
//   excluded_ref_data → Undo Exclusion, and only for MANUAL exclusions.
//   queued / pushed / failed / dead_letter / push_queue →
//              no record-level bulk actions. Remediation for failures lives
//              in the Push Queue panel (retry / force retry / void).
//   all      → ONE button at a time, derived from the selected records'
//              statuses. If any selected record is `pending`, show Approve
//              (the workflow's earlier step). Once all selected records are
//              `approved`, show Push to NS. Selections of pure gap/pushed/etc.
//              show nothing — the user should switch to the specific tab.
//   manually_handled → Undo only. The status is terminal against every
//              automatic pass; this is the one explicit escape hatch so a
//              mis-click isn't permanent. Undo returns each record to the
//              status it came FROM (Pending → Pending, Ops Review → Ops
//              Review) — see billing_records.unmark_manually_handled.
const NO_ACTIONS = {
  approve: false, push: false, ack: false, void: false, retry: false,
  repush: false, markHandled: false, unhandle: false,
  markExcluded: false, unexclude: false,
};

const TAB_ACTIONS = {
  pending:    { ...NO_ACTIONS, approve: true, markHandled: true, markExcluded: true },
  approved:   { ...NO_ACTIONS, push: true },
  gap:        { ...NO_ACTIONS, markExcluded: true },
  // Ops Review intentionally has no Acknowledge — drift always requires
  // explicit Void, Re-push, or (2026-07-28) Mark Handled so subsequent drift
  // on the same punch doesn't get silently suppressed.
  ops_review: { ...NO_ACTIONS, void: true, repush: true, markHandled: true },
  pushed:     { ...NO_ACTIONS, void: true },
  failed:     { ...NO_ACTIONS, retry: true },
  manually_handled:  { ...NO_ACTIONS, unhandle: true },
  excluded_ref_data: { ...NO_ACTIONS, unexclude: true },
};

// Exported because the single-record page at /records/<id> asks the same
// question of one status. Keeping it here — rather than a second table of
// "what can I do to a pushed record?" — is what stops the two surfaces from
// drifting apart the next time a status or a rule is added.
export function actionsForStatuses(selectedStatuses, selectedExclusionReasons) {
  return deriveAllTabAction(selectedStatuses, selectedExclusionReasons);
}

function deriveAllTabAction(selectedStatuses, selectedExclusionReasons) {
  const markHandled  = allEligibleForManualHandling(selectedStatuses);
  const markExcluded = allEligibleForManualExclusion(selectedStatuses);
  if (selectedStatuses.size === 1 && selectedStatuses.has('manually_handled')) {
    return { ...NO_ACTIONS, unhandle: true };
  }
  if (selectedStatuses.size === 1 && selectedStatuses.has('excluded_ref_data')) {
    return { ...NO_ACTIONS, unexclude: allManuallyExcluded(selectedExclusionReasons) };
  }
  if (selectedStatuses.has('pending')) {
    return { ...NO_ACTIONS, approve: true, markHandled, markExcluded };
  }
  if (selectedStatuses.size === 1 && selectedStatuses.has('approved')) {
    return { ...NO_ACTIONS, push: true };
  }
  if (selectedStatuses.size === 1 && selectedStatuses.has('gap')) {
    return { ...NO_ACTIONS, markExcluded: true };
  }
  // Mixed-or-pure source_deleted/source_modified → Ops Review actions.
  // Re-push is only valid for source_modified (there's no source data to
  // push when the punch was deleted upstream). Acknowledge was removed
  // 2026-05-09 — drift always demands a corrective action.
  const opsOnly = [...selectedStatuses].every(
    (s) => s === 'source_deleted' || s === 'source_modified',
  );
  if (selectedStatuses.size > 0 && opsOnly) {
    const allModified = [...selectedStatuses].every((s) => s === 'source_modified');
    return { ...NO_ACTIONS, void: true, repush: allModified, markHandled };
  }
  if (selectedStatuses.size === 1 && selectedStatuses.has('pushed')) {
    return { ...NO_ACTIONS, void: true };
  }
  if (selectedStatuses.size === 1 && selectedStatuses.has('failed')) {
    return { ...NO_ACTIONS, retry: true };
  }
  return { ...NO_ACTIONS };
}

// Ops Review tab itself uses the same per-selection derivation so the
// Re-push button is hidden when source_deleted records are selected.
// (TAB_ACTIONS gives the static "what's possible on this tab" upper bound;
//  this filter narrows it to "what's safe for this exact selection".)
function refineOpsReviewActions(allowed, selectedStatuses) {
  if (!selectedStatuses || selectedStatuses.size === 0) return allowed;
  const allModified = [...selectedStatuses].every((s) => s === 'source_modified');
  return { ...allowed, repush: allowed.repush && allModified };
}

// "Select all in filter" selects records that were never rendered, so the
// per-selection derivation above has nothing honest to work from. Fall back to
// the TAB's static action set — what is possible for that status by
// construction — and let the backend's per-action eligibility screen (plus the
// preview count in the confirm dialog) handle the rest.
//
// The All tab is the exception: its filter spans every status, so a filter-wide
// action there is only offered once the user narrows to ONE status. Otherwise
// "approve everything matching" would mean something different for every row,
// and no dialog could describe it.
function deriveFilterModeActions(active, filterStatus) {
  if (active !== 'all') return TAB_ACTIONS[active] || NO_ACTIONS;
  if (!filterStatus) return NO_ACTIONS;
  return TAB_ACTIONS[filterStatus] || NO_ACTIONS;
}

export function RecordsTabs({
  active, onChange, selectedCount,
  onApprove, onPush, onAcknowledge, onVoid, onRetry, onRepush,
  onMarkHandled, onUnhandle, onMarkExcluded, onUnexclude,
  selectedStatuses,        // Set<string> — statuses of currently selected records (for All tab)
  selectedExclusionReasons, // Set<string> — exclusion_reason of selected records (Excluded tab)
  selectAllMatching,       // true when the selection is a whole filter, not ticked rows
  filterStatus,            // All tab only: the status the user narrowed to, if any
  nsConflict,              // {label, eta_minutes, ...} when an NS-touching activity is running, else null
  mutating,                // null | string label — when set, disable buttons + show "Working: <label>"
}) {
  const { hasPermission } = useAuth();
  const canApprove = hasPermission(Permission.APPROVE_RECORDS);
  const canPush    = hasPermission(Permission.PUSH_TO_NETSUITE);

  let allowed;
  if (selectAllMatching) {
    allowed = deriveFilterModeActions(active, filterStatus);
  } else if (active === 'all') {
    allowed = deriveAllTabAction(
      selectedStatuses || new Set(),
      selectedExclusionReasons || new Set(),
    );
  } else if (active === 'ops_review') {
    allowed = refineOpsReviewActions(
      TAB_ACTIONS.ops_review,
      selectedStatuses || new Set(),
    );
  } else if (active === 'excluded_ref_data') {
    // The tab mixes engine-derived and manual exclusions; only the manual ones
    // can be undone, so narrow the static tab action to this exact selection.
    allowed = {
      ...TAB_ACTIONS.excluded_ref_data,
      unexclude: allManuallyExcluded(selectedExclusionReasons),
    };
  } else {
    allowed = TAB_ACTIONS[active] || NO_ACTIONS;
  }

  const showApprove = allowed.approve && canApprove;
  const showPush    = allowed.push    && canPush;
  const showAck     = allowed.ack     && canApprove;
  const showVoid    = allowed.void    && canPush;
  const showRetry   = allowed.retry   && canPush;
  const showRepush  = allowed.repush  && canPush;
  // Manual handling is a review decision, not a NetSuite operation — it
  // gates on APPROVE_RECORDS, same as Approve/Acknowledge.
  // (The pending + ops_review tabs contain only eligible statuses by
  //  construction; the All tab's eligibility is resolved in
  //  deriveAllTabAction, so no extra check is needed here.)
  const showMarkHandled  = allowed.markHandled  && canApprove;
  const showUnhandle     = allowed.unhandle     && canApprove;
  const showMarkExcluded = allowed.markExcluded && canApprove;
  const showUnexclude    = allowed.unexclude    && canApprove;
  const showActionBar = (showApprove || showPush || showAck || showVoid || showRetry
    || showRepush || showMarkHandled || showUnhandle
    || showMarkExcluded || showUnexclude) && selectedCount > 0;

  // Disable the Push button while NS is in use. Tooltip explains why so
  // users don't think the button is broken.
  const pushDisabled = !!nsConflict;
  const pushTooltip = nsConflict
    ? `${nsConflict.label || 'NetSuite'} in progress${nsConflict.eta_minutes ? ` (~${nsConflict.eta_minutes} min)` : ''} — sync will resume when it finishes.`
    : undefined;

  return (
    <div
      style={{
        display: 'flex', gap: 0, marginBottom: 20,
        borderBottom: '2px solid #dee2e6', alignItems: 'flex-end',
      }}
    >
      {STATUS_TABS.map((f) => (
        <TabButton key={f.val} active={active === f.val} onClick={() => onChange(f.val)}>
          {f.label}
        </TabButton>
      ))}

      <div style={{ flex: 1 }} />

      {/* Filter-wide selection on the All tab, no status chosen: every action
          would mean something different per row, so ask for a status first. */}
      {selectAllMatching && active === 'all' && !filterStatus && selectedCount > 0 && (
        <div style={{
          fontSize: 12, color: '#8a4b00', paddingBottom: 10, maxWidth: 420,
          textAlign: 'right',
        }}>
          Pick a single status in the <em>Status</em> filter to act on every
          record matching this filter.
        </div>
      )}

      {showActionBar && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 8 }}>
          <span style={{ fontSize: 13, color: '#555' }}>
            {mutating ? (
              <span style={{ color: '#0a4ea0' }}>
                <span className="spinner-inline" /> {mutating}…
              </span>
            ) : (
              `${selectedCount} selected`
            )}
          </span>
          {showApprove && (
            <button
              className="btn btn-sm btn-success"
              onClick={onApprove}
              disabled={!!mutating}
              style={mutating ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Approving' ? 'Approving…' : 'Approve'}
            </button>
          )}
          {showPush && (
            <button
              className="btn btn-sm btn-primary"
              onClick={onPush}
              disabled={pushDisabled || !!mutating}
              title={pushTooltip}
              style={(pushDisabled || mutating) ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Syncing to NetSuite' ? 'Syncing…' : 'Sync to NS'}
            </button>
          )}
          {showAck && (
            <button
              className="btn btn-sm btn-warning"
              onClick={onAcknowledge}
              disabled={!!mutating}
              title="Mark these records as reviewed; they leave the Ops Review queue."
              style={mutating ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Acknowledging' ? 'Acknowledging…' : 'Acknowledge'}
            </button>
          )}
          {showVoid && (
            <button
              className="btn btn-sm btn-danger"
              onClick={onVoid}
              disabled={pushDisabled || !!mutating}
              title={pushTooltip || "Void every NetSuite TimeBill for these records and return them to Pending."}
              style={(pushDisabled || mutating) ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Voiding in NetSuite' ? 'Voiding…' : 'Void'}
            </button>
          )}
          {showRetry && (
            <button
              className="btn btn-sm btn-primary"
              onClick={onRetry}
              disabled={pushDisabled || !!mutating}
              title={pushTooltip || "Re-push these failed records to NetSuite."}
              style={(pushDisabled || mutating) ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Retrying sync' ? 'Retrying…' : 'Retry'}
            </button>
          )}
          {showMarkHandled && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={onMarkHandled}
              disabled={!!mutating}
              title="Billed outside this app (manual NS entry / credit note). Records move to Manually Handled and stay there — no reprocess, drift check or Ops Review will move them again."
              style={mutating ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Marking handled' ? 'Marking…' : 'Mark Handled'}
            </button>
          )}
          {showMarkExcluded && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={onMarkExcluded}
              disabled={!!mutating}
              title="Not billable — a reason the engine can't know (duplicate entry, goodwill, internal work miscoded). Records move to Excluded and stay there through every reprocess."
              style={mutating ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Excluding' ? 'Excluding…' : 'Mark Excluded'}
            </button>
          )}
          {showUnexclude && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={onUnexclude}
              disabled={!!mutating}
              title="Undo a manual exclusion — back to Pending or Gaps. Engine-derived exclusions can't be undone here; fix the underlying rule or reference data instead."
              style={mutating ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Undoing exclusion' ? 'Undoing…' : 'Undo Exclusion'}
            </button>
          )}
          {showUnhandle && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={onUnhandle}
              disabled={!!mutating}
              title="Undo — each record goes back to where it came from: Pending records to Pending, Ops Review records to Ops Review (they still have a live NetSuite TimeBill)."
              style={mutating ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Undoing' ? 'Undoing…' : 'Undo Handling'}
            </button>
          )}
          {showRepush && (
            <button
              className="btn btn-sm btn-primary"
              onClick={onRepush}
              disabled={pushDisabled || !!mutating}
              title={pushTooltip || "Void existing TimeBills + push current line items so NetSuite reflects the latest engine state."}
              style={(pushDisabled || mutating) ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {mutating === 'Re-syncing in NetSuite' ? 'Re-syncing…' : 'Re-sync'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
